import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signInAnonymously, linkWithCredential, EmailAuthProvider, sendEmailVerification } from "firebase/auth";
import { doc, setDoc, updateDoc, getDoc, serverTimestamp, deleteField } from "firebase/firestore";
import { nanoid } from "nanoid";
import slug from "slug";
import { Music, MapPin, ListChecks, UserPlus, Check, ChevronRight, ChevronLeft, ArrowRight } from "lucide-react";

import { auth, db } from "../../firebase.js";
import Logo from "../../components/Logo.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";
import StatusPanel from "../../components/StatusPanel.js";
import {
  CATEGORIES,
  PROVINCES,
  SOORT_ACT,
  SOORT_CATERING,
  FOTO_VIDEO_DIENSTEN,
  STYLING_SPECIALISATIES,
  VOERTUIG_SOORTEN,
  REISKOSTEN,
  STRAAL_OPTIES,
  detailsForCategory,
  prune,
} from "../../service.js";

const STORAGE_KEY_ID = "onboarding-service-id";
const STORAGE_KEY_STEP = "onboarding-service-step";

const STEPS = [
  { id: 1, label: "Categorie", icon: Music },
  { id: 2, label: "Bedrijf & werkgebied", icon: MapPin },
  { id: 3, label: "Details", icon: ListChecks },
  { id: 4, label: "Account", icon: UserPlus },
  { id: 5, label: "Klaar", icon: Check },
];

// Laatste stap (bevestiging). Losgetrokken zodat de nummering op één plek staat.
const LAST_STEP = 5;

function StepSidebar({ currentStep }) {
  return (
    <div className="hidden md:flex md:flex-col md:w-sidebar md:fixed md:inset-y-0 md:left-0 bg-bg-sidebar">
      <div className="p-6 mb-2">
        <span className="text-white"><Logo /></span>
      </div>
      <nav className="px-4 space-y-1">
        {STEPS.map((s) => {
          const completed = s.id < currentStep;
          const active = s.id === currentStep;
          return (
            <div
              key={s.id}
              className={[
                "flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-all duration-fast",
                active ? "bg-accent-subtle text-accent" : completed ? "text-[rgba(255,255,255,0.8)]" : "text-[rgba(255,255,255,0.35)]",
              ].join(" ")}
            >
              <span
                className={[
                  "w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold shrink-0",
                  active ? "bg-accent text-white" : completed ? "bg-success text-white" : "bg-[rgba(255,255,255,0.1)] text-[rgba(255,255,255,0.4)]",
                ].join(" ")}
              >
                {completed ? <Check size={12} strokeWidth={3} /> : s.id}
              </span>
              <span>{s.label}</span>
            </div>
          );
        })}
      </nav>
      <div className="mt-auto p-6">
        <p className="text-xs text-[rgba(255,255,255,0.3)] leading-relaxed">
          Heb je al een account?{" "}
          <Link to="/login" className="text-[rgba(255,255,255,0.6)] hover:text-white underline underline-offset-2">Inloggen</Link>
        </p>
      </div>
    </div>
  );
}

function SignupService() {
  const navigate = useNavigate();

  const [authReady, setAuthReady] = useState(false);
  const [step, setStep] = useState(0);
  const [id, setId] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const [categorie, setCategorie] = useState("");
  const [bedrijfsnaam, setBedrijfsnaam] = useState("");
  const [beschrijving, setBeschrijving] = useState("");
  const [contact, setContact] = useState({ email: "", telefoon: "", website: "", kvk: "" });
  // Werkgebied: "landelijk" | "provincies" | "straal".
  const [werkgebiedMode, setWerkgebiedMode] = useState("landelijk");
  const [provincies, setProvincies] = useState([]);
  const [vestiging, setVestiging] = useState({ postcode: "", plaats: "", provincie: "" });
  const [straalKm, setStraalKm] = useState(50);
  const [reiskosten, setReiskosten] = useState("");

  // Categorie-specifiek.
  const [soortAct, setSoortAct] = useState("");
  const [soortCatering, setSoortCatering] = useState([]);
  const [diensten, setDiensten] = useState([]);
  const [specialisaties, setSpecialisaties] = useState([]);
  const [assortiment, setAssortiment] = useState([""]);
  const [voertuigen, setVoertuigen] = useState([{ soort: "limousine", capaciteit: "" }]);
  const [dienst, setDienst] = useState("");

  const [accountEmail, setAccountEmail] = useState("");
  const [accountPassword, setAccountPassword] = useState("");
  const [accountPasswordConfirm, setAccountPasswordConfirm] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    async function initAuth() {
      try {
        // Alleen anoniem inloggen als er nog geen sessie is; een dienst die de
        // onboarding hervat, houdt zijn sessie.
        if (!auth.currentUser) await signInAnonymously(auth);
        const savedId = localStorage.getItem(STORAGE_KEY_ID);
        if (savedId) {
          const savedStep = parseInt(localStorage.getItem(STORAGE_KEY_STEP), 10);
          setId(savedId);
          try {
            const snap = await getDoc(doc(db, "services", savedId));
            if (snap.exists()) hydrate(savedId, snap.data(), savedStep);
            else setStep(savedStep > 0 && savedStep <= LAST_STEP ? savedStep : 1);
          } catch (_e) {
            setStep(savedStep > 0 && savedStep <= LAST_STEP ? savedStep : 1);
          }
        }
      } catch (err) {
        console.error("Anonymous auth failed:", err);
        setError("Er is een fout opgetreden. Probeer de pagina te vernieuwen.");
      }
      setAuthReady(true);
    }

    function hydrate(docId, data, savedStep) {
      setId(docId);
      setCategorie(data.categorie || "");
      setBedrijfsnaam(data.bedrijfsnaam || "");
      setBeschrijving(data.beschrijving || "");
      if (data.contact) setContact({ email: "", telefoon: "", website: "", kvk: "", ...data.contact });
      if (data.werkgebied) {
        const w = data.werkgebied;
        if (w.landelijk) setWerkgebiedMode("landelijk");
        else if (w.straalKm) setWerkgebiedMode("straal");
        else setWerkgebiedMode("provincies");
        setProvincies(w.provincies || []);
        if (w.straalKm) setStraalKm(w.straalKm);
        setReiskosten(w.reiskosten || "");
      }
      if (data.vestigingsplaats) {
        setVestiging({
          postcode: data.vestigingsplaats.postcode || "",
          plaats: data.vestigingsplaats.plaats || "",
          provincie: data.vestigingsplaats.provincie || "",
        });
      }
      setStep(savedStep > 0 && savedStep <= LAST_STEP ? savedStep : 1);
    }

    initAuth();
  }, []);

  function persistStep(n) {
    localStorage.setItem(STORAGE_KEY_STEP, String(n));
  }

  function toggle(list, setList, value) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  // Gedeelde opslaan-en-doorgaan afhandeling voor stap 1 t/m 3: houdt de
  // pending/error-state consistent en zorgt dat pending altijd wordt gereset.
  async function saveAndAdvance(saveFn, next) {
    setError("");
    setPending(true);
    try {
      await saveFn();
      setStep(next);
      persistStep(next);
    } catch (err) {
      console.error(err);
      setError("Er is een fout opgetreden. Probeer het opnieuw.");
    } finally {
      setPending(false);
    }
  }

  if (!authReady) {
    return (
      <div className="flex items-center justify-center h-screen bg-bg-base">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  async function handleStep1() {
    if (!categorie) return setError("Kies een categorie.");
    const docId = id || nanoid(10).toLowerCase();
    await saveAndAdvance(async () => {
      if (id) {
        await updateDoc(doc(db, "services", docId), { categorie });
      } else {
        await setDoc(doc(db, "services", docId), {
          type: "service",
          published: false,
          uid: auth.currentUser.uid,
          createdAt: serverTimestamp(),
          categorie,
          advertentie: { plan: "gratis", status: "aangevraagd", bron: "zelf-aangemeld" },
        });
      }
      setId(docId);
      localStorage.setItem(STORAGE_KEY_ID, docId);
    }, 2);
  }

  async function handleStep2() {
    if (!bedrijfsnaam.trim()) return setError("Vul je bedrijfsnaam in.");
    if (beschrijving.trim().length < 20) return setError("Geef een beschrijving van minimaal 20 tekens.");
    if (!contact.email.trim()) return setError("Vul je e-mailadres in.");

    let werkgebied;
    let vestigingsplaats;
    if (werkgebiedMode === "provincies") {
      if (!provincies.length) return setError("Kies minimaal één provincie.");
      werkgebied = { landelijk: false, provincies };
    } else if (werkgebiedMode === "straal") {
      const postcode = vestiging.postcode.trim().toUpperCase().replace(/\s+/g, " ");
      if (!/^[1-9][0-9]{3}\s?[A-Z]{2}$/.test(postcode)) return setError("Vul een geldige postcode in (bijv. 1234 AB).");
      if (!vestiging.plaats.trim()) return setError("Vul je plaats in.");
      if (!vestiging.provincie) return setError("Kies je provincie.");
      // straalKm/reiskosten in werkgebied; coördinaten worden server-side
      // (algolia-services functie) uit de vestigingsplaats afgeleid.
      werkgebied = prune({ landelijk: false, straalKm, reiskosten });
      vestigingsplaats = prune({ postcode, plaats: vestiging.plaats.trim(), provincie: vestiging.provincie });
    } else {
      werkgebied = { landelijk: true };
    }

    const generatedSlug = slug(bedrijfsnaam, { lower: true });
    await saveAndAdvance(
      () =>
        updateDoc(doc(db, "services", id), {
          bedrijfsnaam: bedrijfsnaam.trim(),
          slug: generatedSlug || `service-${id.replace(/[^a-z0-9]/g, "")}`,
          beschrijving: beschrijving.trim(),
          contact: prune(contact),
          werkgebied,
          // Alleen in straal-modus bewaren; anders een eerder gezette
          // vestigingsplaats opruimen zodat de zoekindex geen oude coördinaten houdt.
          vestigingsplaats: vestigingsplaats || deleteField(),
        }),
      3,
    );
  }

  async function handleStep3() {
    const details = detailsForCategory(categorie, { soortAct, soortCatering, diensten, specialisaties, assortiment, voertuigen, dienst });
    if (!details) return setError("Vul de gevraagde gegevens voor je categorie in.");
    await saveAndAdvance(() => updateDoc(doc(db, "services", id), { details }), 4);
  }

  // Zet de vermelding klaar voor handmatige controle. Er is bewust geen Stripe-
  // stap: aanmeldingen komen binnen als "te-beoordelen" en worden handmatig
  // beoordeeld/gepubliceerd via de admin ("Diensten goedkeuren").
  async function submitForReview() {
    await updateDoc(doc(db, "services", id), {
      "advertentie.status": "te-beoordelen",
      "advertentie.plan": "gratis",
    });
  }

  async function handleAccount(e) {
    e.preventDefault();
    setError("");
    if (!accountEmail.trim()) return setError("Vul je e-mailadres in.");
    if (accountPassword.length < 8) return setError("Het wachtwoord moet minimaal 8 tekens zijn.");
    if (accountPassword !== accountPasswordConfirm) return setError("De wachtwoorden komen niet overeen.");
    if (!agreedToTerms) return setError("Je moet akkoord gaan met de voorwaarden.");
    setPending(true);
    try {
      // Als het account al is aangemaakt (bv. deze stap opnieuw ingediend na een
      // stap-terug of herladen), sla het koppelen over en ga direct door.
      const alreadyLinked = auth.currentUser?.providerData?.some((p) => p.providerId === "password");
      if (!alreadyLinked) {
        const credential = EmailAuthProvider.credential(accountEmail.trim(), accountPassword);
        await linkWithCredential(auth.currentUser, credential);
        await sendEmailVerification(auth.currentUser);
      }
      await submitForReview();
      setStep(LAST_STEP);
      persistStep(LAST_STEP);
    } catch (err) {
      console.error(err);
      // Provider al gekoppeld = account bestaat al; behandel als succes.
      if (err.code === "auth/provider-already-linked") {
        try {
          await submitForReview();
        } catch (_e) {
          // Indienen kan later alsnog via "Mijn dienst"; blokkeer de flow niet.
        }
        setStep(LAST_STEP);
        persistStep(LAST_STEP);
      } else if (err.code === "auth/email-already-in-use" || err.code === "auth/credential-already-in-use") {
        setError("Dit e-mailadres is al in gebruik. Log in met je bestaande account.");
      } else if (err.code === "auth/weak-password") setError("Het wachtwoord is te zwak.");
      else if (err.code === "auth/invalid-email") setError("Het e-mailadres is niet geldig.");
      else setError("Er is een fout opgetreden. Probeer het opnieuw.");
    }
    setPending(false);
  }

  function goBack() {
    setError("");
    const prev = step - 1;
    setStep(prev);
    persistStep(prev);
  }

  function renderStep() {
    switch (step) {
      case 0:
        return (
          <div>
            <h2 className="text-3xl font-heading font-bold mb-2">Meld je dienst aan op Feestjegeven</h2>
            <p className="text-base text-text-secondary mb-8">
              Bereik mensen die op zoek zijn naar jouw dienst voor hun feest of evenement. Aanmelden is gratis - we zetten je vermelding na een korte controle online.
            </p>

            <div className="mb-8">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-text-tertiary mb-4">Zo werkt het</h3>
              <div className="space-y-3">
                {[
                  { n: 1, t: "Profiel invullen", d: "Vertel over je dienst en werkgebied." },
                  { n: 2, t: "Account aanmaken", d: "Hiermee beheer je later je pagina." },
                  { n: 3, t: "Wij zetten je online", d: "Na een korte controle staat je vermelding online." },
                ].map((s) => (
                  <div key={s.n} className="flex gap-3">
                    <span className="w-7 h-7 rounded-full bg-accent-subtle text-accent flex items-center justify-center text-sm font-semibold shrink-0">{s.n}</span>
                    <div>
                      <p className="text-sm font-medium">{s.t}</p>
                      <p className="text-sm text-text-secondary">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {error && <div className="mb-4"><Message text={error} /></div>}
            <Button label="Start je aanmelding" onClick={() => { setStep(1); persistStep(1); }} icon={ArrowRight} />
            <p className="text-xs text-text-tertiary mt-4">
              Heb je al een account?{" "}
              <Link to="/login?type=service" className="text-accent hover:underline underline-offset-2">Inloggen</Link>
            </p>
          </div>
        );
      case 1:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Wat voor dienst bied je aan?</h2>
            <p className="text-sm text-text-secondary mb-6">Kies de categorie die het beste bij je dienst past.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setCategorie(c.value)}
                  className={[
                    "text-left px-4 py-3 rounded-sm border transition-all duration-fast text-sm",
                    categorie === c.value ? "bg-accent-subtle border-accent/30 text-accent font-medium" : "bg-bg-surface border-border text-text-secondary hover:border-border-hover",
                  ].join(" ")}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {error && <div className="mb-4"><Message text={error} /></div>}
            <Button label="Volgende" onClick={handleStep1} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
          </div>
        );
      case 2:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Over je bedrijf</h2>
            <p className="text-sm text-text-secondary mb-6">Deze gegevens komen op je dienstpagina.</p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="label-base">Bedrijfsnaam <span className="text-error">*</span></label>
                <input type="text" value={bedrijfsnaam} onChange={(e) => setBedrijfsnaam(e.target.value)} className="input-base" />
              </div>
              <div>
                <label className="label-base">Beschrijving <span className="text-error">*</span></label>
                <textarea value={beschrijving} onChange={(e) => setBeschrijving(e.target.value)} rows={4} className="input-base" placeholder="Vertel wat je aanbiedt (min. 20 tekens)" />
              </div>
              <div>
                <label className="label-base">Contactemail <span className="text-error">*</span></label>
                <input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} className="input-base" />
                <p className="text-xs text-text-tertiary mt-1">Telefoon, website en KvK vul je later in bij Mijn dienst.</p>
              </div>
              <div>
                <label className="label-base mb-2 block">Werkgebied <span className="text-error">*</span></label>
                <div className="flex flex-wrap gap-2 mb-3">
                  {[
                    { value: "landelijk", label: "Heel Nederland" },
                    { value: "provincies", label: "Provincies" },
                    { value: "straal", label: "Straal rond mijn plaats" },
                  ].map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setWerkgebiedMode(m.value)}
                      className={["px-3 py-1.5 rounded-full border text-sm", werkgebiedMode === m.value ? "bg-accent-subtle border-accent/30 text-accent" : "bg-bg-surface border-border text-text-secondary"].join(" ")}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                {werkgebiedMode === "provincies" && (
                  <div className="flex flex-wrap gap-2">
                    {PROVINCES.map((p) => (
                      <button key={p} type="button" onClick={() => toggle(provincies, setProvincies, p)} className={["px-3 py-1.5 rounded-full border text-sm", provincies.includes(p) ? "bg-accent-subtle border-accent/30 text-accent" : "bg-bg-surface border-border text-text-secondary"].join(" ")}>{p}</button>
                    ))}
                  </div>
                )}
                {werkgebiedMode === "straal" && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="label-base">Postcode <span className="text-error">*</span></label>
                        <input className="input-base" value={vestiging.postcode} onChange={(e) => setVestiging({ ...vestiging, postcode: e.target.value })} placeholder="1234 AB" />
                      </div>
                      <div>
                        <label className="label-base">Plaats <span className="text-error">*</span></label>
                        <input className="input-base" value={vestiging.plaats} onChange={(e) => setVestiging({ ...vestiging, plaats: e.target.value })} placeholder="bijv. Utrecht" />
                      </div>
                      <div>
                        <label className="label-base">Provincie <span className="text-error">*</span></label>
                        <select className="input-base" value={vestiging.provincie} onChange={(e) => setVestiging({ ...vestiging, provincie: e.target.value })}>
                          <option value="">Kies...</option>
                          {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="label-base">Straal</label>
                        <select className="input-base" value={straalKm} onChange={(e) => setStraalKm(Number(e.target.value))}>
                          {STRAAL_OPTIES.map((km) => <option key={km} value={km}>{km} km</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label-base">Reiskosten</label>
                      <select className="input-base" value={reiskosten} onChange={(e) => setReiskosten(e.target.value)}>
                        <option value="">Niet opgegeven</option>
                        {REISKOSTEN.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                    <p className="text-xs text-text-tertiary">We tonen je dienst aan bezoekers die binnen deze straal van je plaats zoeken. Je exacte adres wordt niet getoond.</p>
                  </div>
                )}
              </div>
            </div>
            {error && <div className="mb-4"><Message text={error} /></div>}
            <div className="flex gap-3">
              <Button variant="secondary" label="Terug" onClick={goBack} icon={ChevronLeft} />
              <Button label="Volgende" onClick={handleStep2} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <h2 className="text-2xl font-heading font-bold mb-2">Details</h2>
            <p className="text-sm text-text-secondary mb-6">Nog wat specifieke informatie over je aanbod.</p>
            <div className="space-y-4 mb-6">
              {categorie === "muziek-entertainment" && (
                <div>
                  <label className="label-base">Soort act <span className="text-error">*</span></label>
                  <select value={soortAct} onChange={(e) => setSoortAct(e.target.value)} className="input-base"><option value="">Kies...</option>{SOORT_ACT.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                </div>
              )}
              {categorie === "catering-food" && (
                <ChipGroup label="Soort catering" options={SOORT_CATERING} selected={soortCatering} onToggle={(v) => toggle(soortCatering, setSoortCatering, v)} />
              )}
              {categorie === "foto-video" && (
                <ChipGroup label="Diensten" options={FOTO_VIDEO_DIENSTEN} selected={diensten} onToggle={(v) => toggle(diensten, setDiensten, v)} />
              )}
              {categorie === "styling-decoratie" && (
                <ChipGroup label="Specialisaties" options={STYLING_SPECIALISATIES} selected={specialisaties} onToggle={(v) => toggle(specialisaties, setSpecialisaties, v)} />
              )}
              {categorie === "verhuur" && (
                <div>
                  <label className="label-base">Assortiment <span className="text-error">*</span></label>
                  {assortiment.map((a, i) => (
                    <input key={i} className="input-base mb-2" placeholder="Artikelnaam" value={a} onChange={(e) => setAssortiment(assortiment.map((x, j) => (j === i ? e.target.value : x)))} />
                  ))}
                  <button type="button" className="text-sm text-accent font-medium" onClick={() => setAssortiment([...assortiment, ""])}>+ Artikel toevoegen</button>
                </div>
              )}
              {categorie === "vervoer" && (
                <div>
                  <label className="label-base">Voertuigen <span className="text-error">*</span></label>
                  {voertuigen.map((v, i) => (
                    <div key={i} className="grid grid-cols-2 gap-2 mb-2">
                      <select className="input-base" value={v.soort} onChange={(e) => setVoertuigen(voertuigen.map((x, j) => (j === i ? { ...x, soort: e.target.value } : x)))}>{VOERTUIG_SOORTEN.map((s) => <option key={s} value={s}>{s}</option>)}</select>
                      <input className="input-base" type="number" placeholder="Capaciteit" value={v.capaciteit} onChange={(e) => setVoertuigen(voertuigen.map((x, j) => (j === i ? { ...x, capaciteit: e.target.value } : x)))} />
                    </div>
                  ))}
                  <button type="button" className="text-sm text-accent font-medium" onClick={() => setVoertuigen([...voertuigen, { soort: "limousine", capaciteit: "" }])}>+ Voertuig toevoegen</button>
                </div>
              )}
              {categorie === "overige-diensten" && (
                <div>
                  <label className="label-base">Dienst <span className="text-error">*</span></label>
                  <input className="input-base" value={dienst} onChange={(e) => setDienst(e.target.value)} placeholder="bijv. ceremoniespreker" />
                </div>
              )}
            </div>
            {error && <div className="mb-4"><Message text={error} /></div>}
            <div className="flex gap-3">
              <Button variant="secondary" label="Terug" onClick={goBack} icon={ChevronLeft} />
              <Button label="Volgende" onClick={handleStep3} pending={pending} loadingLabel="Een momentje..." icon={ChevronRight} />
            </div>
          </div>
        );
      case 4:
        return (
          <form onSubmit={handleAccount}>
            <h2 className="text-2xl font-heading font-bold mb-2">Maak je account aan</h2>
            <p className="text-sm text-text-secondary mb-6">Hiermee beheer je later je dienstpagina.</p>
            <div className="space-y-4 mb-6">
              <div>
                <label className="label-base">Login-emailadres <span className="text-error">*</span></label>
                <input type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} autoComplete="email" required className="input-base" />
              </div>
              <div>
                <label className="label-base">Wachtwoord <span className="text-error">*</span></label>
                <input type="password" value={accountPassword} onChange={(e) => setAccountPassword(e.target.value)} autoComplete="new-password" minLength="8" required className="input-base" />
              </div>
              <div>
                <label className="label-base">Wachtwoord bevestigen <span className="text-error">*</span></label>
                <input type="password" value={accountPasswordConfirm} onChange={(e) => setAccountPasswordConfirm(e.target.value)} autoComplete="new-password" minLength="8" required className="input-base" />
              </div>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={agreedToTerms} onChange={(e) => setAgreedToTerms(e.target.checked)} className="mt-1 accent-accent" />
                <span className="text-sm text-text-secondary">Ik ga akkoord met de <a href="/static/GEBRUIKSOVEREENKOMST_FG.pdf" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">voorwaarden</a> van Feestjegeven.nl</span>
              </label>
            </div>
            {error && <div className="mb-4"><Message text={error} /></div>}
            <div className="flex gap-3">
              <Button variant="secondary" type="button" label="Terug" onClick={goBack} icon={ChevronLeft} />
              <Button type="submit" label="Aanmelding afronden" pending={pending} loadingLabel="Even geduld..." icon={ArrowRight} />
            </div>
          </form>
        );
      case 5:
        return (
          <div>
            <StatusPanel
              card
              variant="success"
              title="Bedankt! Je aanmelding is compleet"
              actions={
                <>
                  <Button label="Mijn dienst aanvullen" onClick={() => navigate("/mijn-dienst")} icon={ArrowRight} />
                  <button type="button" onClick={() => navigate("/account")} className="text-sm text-accent hover:underline underline-offset-2">Naar mijn account</button>
                </>
              }
            >
              <p className="text-sm text-text-secondary mb-4">
                We hebben je gegevens ontvangen en zetten je vermelding na een korte controle online. Je hoeft nu niets te betalen.
              </p>
              <p className="text-sm text-text-secondary mb-6">
                Maak je pagina alvast compleet met foto's en extra gegevens.
              </p>
            </StatusPanel>
            {error && <div className="mt-4"><Message text={error} /></div>}
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div className="min-h-screen bg-bg-base flex flex-col md:flex-row">
      <StepSidebar currentStep={step} />
      <div className="flex-1 md:ml-sidebar">
        <div className="flex items-start justify-center p-6 sm:p-8 md:p-12 min-h-screen">
          <div className={["w-full animate-fade-in", step === 0 ? "max-w-2xl" : "max-w-lg"].join(" ")}>{renderStep()}</div>
        </div>
      </div>
    </div>
  );
}

function ChipGroup({ label, options, selected, onToggle }) {
  return (
    <div>
      <label className="label-base mb-2 block">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onToggle(o)} className={["px-3 py-1.5 rounded-full border text-sm", selected.includes(o) ? "bg-accent-subtle border-accent/30 text-accent" : "bg-bg-surface border-border text-text-secondary"].join(" ")}>{o}</button>
        ))}
      </div>
    </div>
  );
}

export default SignupService;
